import { useState, useEffect, useCallback, useRef } from 'react';
import QRCode from 'qrcode';
import { deriveItemKey, encrypt, decrypt, generateItemId, PROTOCOL_VERSION, type AADPayload } from '../crypto/crypto';
import { encryptFile, decryptFile } from '../crypto/file-crypto';
import { encryptRoomSecret } from '../crypto/pairing-client';
import { listItems, createItem, deleteItem } from '../api/items';
import { uploadFile, downloadFile } from '../api/files';
import { getRoom, type RoomMetadata } from '../api/rooms';
import { createPairingCode, pollPairingStatus, approvePairing } from '../api/pairing-api';
import { useRoomSecret, buildRoomURL } from '../hooks/useRoomSecret';
import { WSClient, type WSEvent } from '../ws/ws-client';

interface DecryptedItem {
  itemId: string;
  kind: string;
  plaintext: string;
  filename?: string;
  blobId?: string;
  // For file items: raw crypto fields needed to decrypt the blob on download.
  nonce?: string;
  aad?: string;
  ciphertext?: string;
  createdAt: string;
}

export function RoomPage({ roomId }: { roomId: string }) {
  const creds = useRoomSecret();
  const [items, setItems] = useState<DecryptedItem[]>([]);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [room, setRoom] = useState<RoomMetadata | null>(null);
  const [copyStatus, setCopyStatus] = useState<string | null>(null);
  const wsClientRef = useRef<WSClient | null>(null);
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  const loadItems = useCallback(async () => {
    if (!creds) return;
    setLoading(true);
    setError(null);
    try {
      const result = await listItems(roomId, creds.accessToken);
      const decrypted: DecryptedItem[] = [];
      for (const item of result.items) {
        try {
          const key = await deriveItemKey(creds.roomSecret, roomId, item.itemId);
          const plaintext = await decrypt(key, {
            ciphertext: item.ciphertext,
            nonce: item.nonce,
            aad: item.aad,
          });
          // File items: plaintext is "JSON(metadata)" + 0x00 + fileBytes.
          // Extract metadata (up to null byte) for display.
          let filename: string | undefined;
          let displayText = plaintext;
          if (item.kind === 'file') {
            const nullIdx = plaintext.indexOf('\x00');
            const metaJson = nullIdx >= 0 ? plaintext.slice(0, nullIdx) : plaintext;
            try {
              const meta = JSON.parse(metaJson);
              filename = meta.filename;
              displayText = meta.mimeType || '文件';
            } catch {
              // keep raw plaintext as fallback
            }
          }
          decrypted.push({
            itemId: item.itemId,
            kind: item.kind,
            plaintext: displayText,
            filename,
            blobId: item.blobId,
            nonce: item.nonce,
            aad: item.aad,
            ciphertext: item.ciphertext,
            createdAt: item.createdAt,
          });
        } catch {
          decrypted.push({
            itemId: item.itemId,
            kind: item.kind,
            plaintext: '[解密失败]',
            createdAt: item.createdAt,
          });
        }
      }
      setItems(decrypted);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to load items');
    } finally {
      setLoading(false);
    }
  }, [creds, roomId]);

  useEffect(() => {
    if (!creds) return;
    // Load room metadata.
    getRoom(roomId).then(setRoom).catch(() => {});
    // Load items.
    loadItems();

    // Connect WebSocket for real-time updates.
    const wsClient = new WSClient(roomId, creds.accessToken, (evt: WSEvent) => {
      if (evt.type === 'item-added' || evt.type === 'item-deleted') {
        // Reload items on any change.
        loadItems();
      }
    }, () => {
      // On reconnect: reload to catch up on missed events.
      loadItems();
    });
    wsClientRef.current = wsClient;
    wsClient.connect();

    // Also poll every 10 seconds as a fallback for missed WS events.
    const interval = setInterval(loadItems, 10000);

    return () => {
      wsClient.stop();
      clearInterval(interval);
    };
  }, [creds, roomId, loadItems]);

  // Pairing code lifecycle:
  //   - On room entry, try restore code from sessionStorage (survives refresh).
  //   - If no stored code, create one and store it.
  //   - Poll status every 2s; auto-approve on join request.
  //   - Renew every 100s (code TTL is 120s) ONLY if still active.
  // The polling interval is stored in a ref so we can stop/restart it.
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const codeInitializedRef = useRef(false);

  useEffect(() => {
    if (!creds || codeInitializedRef.current) return;
    // Only the host (room creator) generates pairing codes.
    // Joiners (entered via pairing code) don't need to create new codes.
    const isHost = sessionStorage.getItem('crossclip-is-host') === '1';
    if (!isHost) {
      codeInitializedRef.current = true;
      return;
    }
    codeInitializedRef.current = true;

    const startPolling = (code: string) => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      let approved = false;
      pollIntervalRef.current = setInterval(async () => {
        try {
          const status = await pollPairingStatus(code, creds!.accessToken);
          if (status.publicKey && !approved) {
            approved = true;
            const envelope = await encryptRoomSecret(status.publicKey, creds!.roomSecret, creds!.accessToken);
            await approvePairing(code, creds!.accessToken, envelope);
          }
        } catch {
          // Code expired or error - stop polling, will renew below.
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          // Clear the displayed code immediately so the user sees
          // "配对码生成中…" and doesn't try an expired code.
          setPairingCode(null);
        }
      }, 2000);
    };

    const createNewCode = async () => {
      try {
        const resp = await createPairingCode(roomId, creds!.accessToken);
        setPairingCode(resp.code);
        sessionStorage.setItem('crossclip-pairing-code', resp.code);
        startPolling(resp.code);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'failed to create pairing code');
      }
    };

    // Try restore from sessionStorage (survives page refresh).
    const storedCode = sessionStorage.getItem('crossclip-pairing-code');
    if (storedCode && storedCode.length === 6) {
      setPairingCode(storedCode);
      startPolling(storedCode);
    } else {
      void createNewCode();
    }

    // Renew every 10s (check if code expired). Code TTL is 120s, but
    // polling detects expiration and sets pollIntervalRef to null immediately.
    // This interval picks up the expired state and creates a new code quickly.
    const renewInterval = setInterval(async () => {
      if (!pollIntervalRef.current) {
        void createNewCode();
      }
    }, 10000);

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      clearInterval(renewInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creds]);

  useEffect(() => {
    if (!creds) return;
    // Generate QR code for the share URL.
    const url = buildRoomURL(roomId, creds.roomSecret, creds.accessToken);
    QRCode.toDataURL(url, { width: 256, margin: 1 })
      .then(setQrDataUrl)
      .catch(() => {});
  }, [creds, roomId]);

  function handleCopyShareUrl() {
    if (!creds) return;
    const url = buildRoomURL(roomId, creds.roomSecret, creds.accessToken);
    navigator.clipboard.writeText(url).catch(() => {
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    });
    setCopyStatus('链接已复制');
    setTimeout(() => setCopyStatus(null), 2000);
  }

  async function handleSend() {
    if (!creds || !text.trim()) return;
    setError(null);
    try {
      const itemId = generateItemId();
      const key = await deriveItemKey(creds.roomSecret, roomId, itemId);
      const aadPayload: AADPayload = {
        v: PROTOCOL_VERSION,
        roomId,
        itemId,
        kind: 'text',
      };
      const enc = await encrypt(key, text, aadPayload);
      await createItem(roomId, creds.accessToken, {
        itemId,
        kind: 'text',
        nonce: enc.nonce,
        aad: enc.aad,
        ciphertext: enc.ciphertext,
      });
      setText('');
      await loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to send');
    }
  }

  async function handleCopy(text: string) {
    setCopyStatus(null);
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus('已复制');
    } catch {
      // Fallback: create a temporary textarea for manual selection.
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        setCopyStatus('已复制');
      } catch {
        setCopyStatus('复制失败，请手动选择文本');
      }
      document.body.removeChild(ta);
    }
    setTimeout(() => setCopyStatus(null), 2000);
  }

  async function handleDelete(itemId: string) {
    if (!creds) return;
    try {
      await deleteItem(roomId, itemId, creds.accessToken);
      await loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to delete');
    }
  }

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (!creds || !e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    setError(null);
    try {
      const enc = await encryptFile(creds.roomSecret, roomId, file);
      await uploadFile(roomId, creds.accessToken, enc);
      await loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to upload file');
    }
    e.target.value = '';
  }

  async function handleDownload(itemId: string) {
    if (!creds) return;
    const item = items.find((i) => i.itemId === itemId);
    const blobId = item?.blobId;
    if (!blobId || !item) return;
    setError(null);
    try {
      const blobData = await downloadFile(roomId, blobId, creds.accessToken);
      // Decrypt the blob using the item's crypto fields.
      // For file items, the blob ciphertext == item ciphertext (combined
      // metadata+content), so we can pass either. decryptFile re-derives
      // the key from roomSecret+itemId and uses nonce/aad.
      const result = await decryptFile(
        creds.roomSecret,
        roomId,
        itemId,
        blobData,
        item.ciphertext || '',
        item.nonce || '',
        item.aad || '',
      );
      const blob = new Blob([result.content], { type: result.mimeType || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename || item.filename || `item-${itemId}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to download file');
    }
  }

  if (!creds) {
    return (
      <div>
        <h1>CrossClip</h1>
        <p role="alert">无法加载房间密钥。请通过完整链接进入房间。</p>
        <a href="/">返回首页</a>
      </div>
    );
  }

  const isHost = sessionStorage.getItem('crossclip-is-host') === '1';

  return (
    <div>
      <div className="room-header">
        <h1>CrossClip 房间</h1>
        {room && (
          <p>过期：{new Date(room.expiresAt).toLocaleString()}</p>
        )}
      </div>

      <div className="card pairing-section">
        <h2>二维码 / 配对码加入</h2>
        {qrDataUrl && (
          <div style={{ display: 'flex', justifyContent: 'center', margin: '0.5rem 0' }}>
            <img src={qrDataUrl} alt="房间二维码" width={220} height={220} />
          </div>
        )}
        <div className="row" style={{ justifyContent: 'center' }}>
          <button onClick={handleCopyShareUrl} className="secondary">复制链接</button>
          {copyStatus && <span style={{ color: 'var(--success)' }}>{copyStatus}</span>}
        </div>
        {isHost && (
          pairingCode ? (
            <div>
              <div className="pairing-code">{pairingCode}</div>
              <p className="muted">在新设备输入此配对码即可自动加入</p>
            </div>
          ) : (
            <p className="muted">配对码生成中…</p>
          )
        )}
      </div>

      <div className="card">
        <h2>发送内容</h2>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="输入要发送的文本…"
          rows={3}
          aria-label="文本输入"
        />
        <div className="row" style={{ marginTop: '0.5rem' }}>
          <button onClick={handleSend} disabled={!text.trim()}>发送</button>
        </div>
        <div style={{ marginTop: '0.75rem' }}>
          <input
            type="file"
            onChange={handleFileUpload}
            aria-label="文件上传"
          />
        </div>
      </div>

      {error && <p role="alert">{error}</p>}

      <h2>条目 ({items.length})</h2>
      {loading && items.length === 0 ? (
        <p className="muted">加载中…</p>
      ) : items.length === 0 ? (
        <div className="empty-state">暂无条目，发送第一条内容吧</div>
      ) : (
        <ul>
          {items.map((item) => (
            <li key={item.itemId}>
              {item.kind === 'file' ? (
                <div className="item-content">📎 {item.filename || '文件'} <span className="muted">（{item.plaintext}）</span></div>
              ) : (
                <div className="item-content">{item.plaintext}</div>
              )}
              <div className="item-meta">
                {new Date(item.createdAt).toLocaleString()}
              </div>
              <div className="item-actions">
                {item.kind !== 'file' && (
                  <button onClick={() => handleCopy(item.plaintext)} className="small secondary">复制</button>
                )}
                {item.blobId && (
                  <button onClick={() => handleDownload(item.itemId)} className="small secondary">下载</button>
                )}
                <button onClick={() => handleDelete(item.itemId)} className="small danger">删除</button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <a href="/">← 返回首页</a>
    </div>
  );
}
